import sources from './locales/sources.json';
import { PublicLocale, localizedPath } from './locales';
import { PublicMessages, translator } from './publicMessages';

interface DocumentSource {
  slug: string;
  title: string;
  html: string;
  description?: string;
  excerpt?: string;
}
export function sourceFingerprint(page: DocumentSource): string {
  const text = [page.title, page.description || '', page.excerpt || '', page.html].join('\n');
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
  }
  return (hash >>> 0).toString(16);
}
export function hasDocumentTranslation(page: DocumentSource, locale: PublicLocale): boolean {
  return (
    locale === 'en' || (sources as Record<string, string>)[page.slug] === sourceFingerprint(page)
  );
}
const escapeText = (value: string) =>
  value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
/** Only text changes; original markup, source links and attribution are retained. */
export function translateDocument<T extends DocumentSource>(
  page: T,
  locale: PublicLocale,
  messages: PublicMessages,
): T & { contentLocale: PublicLocale } {
  if (locale === 'en' || !hasDocumentTranslation(page, locale)) {
    return { ...page, contentLocale: 'en' };
  }
  const t = translator(messages);
  const html = page.html
    .split(/(<[^>]*>)/g)
    .map((part) => {
      if (part.startsWith('<')) {
        return part.replace(
          /href="(\/[^"]*)"/g,
          (_all, path: string) => `href="${localizedPath(path, locale)}"`,
        );
      }
      const key = part.replace(/\s+/g, ' ').trim();
      const translated = t(key);
      return translated === key
        ? part
        : `${part.match(/^\s*/)?.[0] || ''}${escapeText(translated)}${part.match(/\s*$/)?.[0] || ''}`;
    })
    .join('');
  return {
    ...page,
    title: t(page.title),
    description: page.description ? t(page.description) : undefined,
    excerpt: page.excerpt ? t(page.excerpt) : undefined,
    html,
    contentLocale: locale,
  };
}
